import React, { createContext, useContext, useMemo } from 'react';
import useCropData from 'hooks/useCropData';

interface Crop {
  id: string;
  name: string;
}

interface CropDataContextValue {
  crops?: Crop[];
  loading?: boolean;
}

const CropDataContext = createContext<CropDataContextValue>({});

/**
 * Fetches the crop list (`useCropData`) once, as soon as the Plan wizard
 * mounts, shared via context so both `PlanStepsLayout` (to gate the
 * stepper's Next/Save buttons and swap in a loading placeholder, matching
 * Applications/Work Orders' own edit-loading treatment) and `DetailsStep`
 * (the Crop `<Select>` itself) read the same fetch — mirrors
 * `PropertyTreeContext`'s identical shape for the property tree fetch,
 * rather than each consumer calling `useCropData()` separately and
 * duplicating the request.
 */
export const CropDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { crops, loading } = useCropData() as CropDataContextValue;
  const value = useMemo(() => ({ crops, loading }), [crops, loading]);

  return (
    <CropDataContext.Provider value={value}>
      {children}
    </CropDataContext.Provider>
  );
};

export const useCropDataContext = (): CropDataContextValue =>
  useContext(CropDataContext);
